// frontend/src/utils/availability.js
//
// Available, in the browser.
//
// Available = on hand − committed − unavailable. That is what Shopify means by
// the word: its identity is `on_hand = available + committed + reserved +
// damaged + safety_stock + quality_control`, and its admin renders the last
// four as a single "Unavailable" figure, which `Inventory.inventory_unavailable`
// carries as one number. The backend spells the same rule once, at
// `core.utils.available_expr`.
//
// Why the browser computes it at all when the API already annotates it: on hand
// is EDITABLE on the Inventory grid, and the Projected available cell
// (components/grid/projectedAvailableColumn.js) has to follow the box the
// merchant is typing in. Reading the server's figure would freeze it for the
// whole edit.

/**
 * Available from three quantities, or `null` when it is not computable.
 *
 * **Strictly numeric.** Every term must already be a finite number; this
 * refuses strings, `null` and `undefined` rather than coercing them, because
 * `Number(null)` is 0 and a silent zero here reads every held-back unit as
 * sellable.
 *
 * Normalising input is therefore the CALLER's job: turning the text in a
 * number box into a quantity is the editable grid's `valueParser`, not
 * availability's.
 *
 * `null` means "not computable"; the Projected available column's
 * `numberColumn` type renders it as a dash, never a figure. The API
 * coalesces all three to 0, so a non-number term is a broken contract, and the
 * grid says "unknown" instead of inventing a figure.
 *
 * @param {number} onHand
 * @param {number} committed
 * @param {number} unavailable
 * @returns {number|null}
 */
export function availableFrom(onHand, committed, unavailable) {
  const terms = [onHand, committed, unavailable];
  if (terms.some((n) => typeof n !== 'number' || !Number.isFinite(n))) return null;
  return onHand - committed - unavailable;
}
